import {
    BaseTransitionModel,
} from '@src/slide/domain/ui-model/element-modeles/transition/transition-model/base-transition-model';
import {
    AnimationLabel,
    ImplementationType,
    PrismSubType,
    TransitionTarget, TransitionType,
} from '@src/slide/domain/ui-model/element-modeles/transition/constants';
import { IAnimationSetting, IPrismTransition } from '@src/slide/domain/ui-model/element-modeles/transition/interface';
import { TransitionConfig } from '@src/slide/domain/ui-model/element-modeles/transition/config';
import { SideDirection } from '@src/slide/domain/ui-model/element-modeles/transition/direction/side-direction';
import { SideDirectionType } from '@src/slide/domain/ui-model/element-modeles/transition/direction/constants';

/**
 * 包含四种动画类型，通过子属性区分
 * 框、旋转、立方体、轨道
 */
export class PrismTransitionModel extends BaseTransitionModel<IPrismTransition> implements IPrismTransition {
    public type = TransitionType.PRISM;
    public direction: SideDirection;
    public isInverted?: boolean;
    public isContent?: boolean;

    public getAnimationSetting(isSwitchForward?: boolean): IAnimationSetting[] {
        const direction = this.direction.getDirectionType(isSwitchForward);
        if (this.subType === PrismSubType.CUBE || this.subType === PrismSubType.CONTENT_CUBE) {
            return this.getCubeSetting(direction);
        }
        return [];
    }

    public getImplementationType(): ImplementationType {
        return ImplementationType.PRESERVE_3D;
    }

    /**
     * 是否支持播放该类型动画
     */
    public get isSupported(): boolean {
        return super.isSupported && this.subType === PrismSubType.CUBE;
    }

    protected get subType(): PrismSubType {
        if (this.isInverted && this.isContent) {
            return PrismSubType.CONTENT_BOX;
        }
        if (this.isInverted) {
            return PrismSubType.BOX;
        }
        if (this.isContent) {
            return PrismSubType.CONTENT_CUBE;
        }
        return PrismSubType.CUBE;
    }

    private getCubeSetting(direction: SideDirectionType): IAnimationSetting[] {
        const duration = this.animationDuration / 2;
        if (direction === SideDirectionType.LEFT) {
            return [{
                fromStyle: {
                    rotateY: 0,
                    translateX: 0,
                    ...TransitionConfig.ORIGIN_RIGHT_CENTER,
                },
                toStyle: {
                    rotateY: -45,
                    translateX: -0.5,
                    ...TransitionConfig.ORIGIN_CENTER,
                    duration,
                },
                target: TransitionTarget.EXIT,
            }, {
                fromStyle: {
                    rotateY: -45,
                    translateX: -0.5,
                    ...TransitionConfig.ORIGIN_CENTER,
                },
                toStyle: {
                    rotateY: -90,
                    translateX: -1,
                    ...TransitionConfig.ORIGIN_LEFT_CENTER,
                    duration,
                },
                target: TransitionTarget.EXIT,
                label: AnimationLabel.NEXT,
            }, {
                fromStyle: {
                    rotateY: 90,
                    translateX: 1,
                    ...TransitionConfig.ORIGIN_RIGHT_CENTER,
                },
                toStyle: {
                    rotateY: 45,
                    translateX: 0.5,
                    ...TransitionConfig.ORIGIN_CENTER,
                    duration,
                },
                target: TransitionTarget.ENTER,
            }, {
                fromStyle: {
                    rotateY: 45,
                    translateX: 0.5,
                    ...TransitionConfig.ORIGIN_CENTER,
                    ...TransitionConfig.Z_INDEX_LEVEL_1,
                },
                toStyle: {
                    rotateY: 0,
                    translateX: 0,
                    ...TransitionConfig.ORIGIN_LEFT_CENTER,
                    ...TransitionConfig.Z_INDEX_LEVEL_1,
                    duration,
                },
                target: TransitionTarget.ENTER,
                label: AnimationLabel.NEXT,
            }];
        }
        if (direction === SideDirectionType.UP) {
            return [{
                fromStyle: {
                    rotateX: 0,
                    translateY: 0,
                    ...TransitionConfig.ORIGIN_BOTTOM_CENTER,
                },
                toStyle: {
                    rotateX: 45,
                    translateY: -0.5,
                    ...TransitionConfig.ORIGIN_CENTER,
                    duration,
                },
                target: TransitionTarget.EXIT,
            }, {
                fromStyle: {
                    rotateX: 45,
                    translateY: -0.5,
                    ...TransitionConfig.ORIGIN_CENTER,
                },
                toStyle: {
                    rotateX: 90,
                    translateY: -1,
                    ...TransitionConfig.ORIGIN_TOP_CENTER,
                    duration,
                },
                target: TransitionTarget.EXIT,
                label: AnimationLabel.NEXT,
            }, {
                fromStyle: {
                    rotateX: -90,
                    translateY: 1,
                    ...TransitionConfig.ORIGIN_BOTTOM_CENTER,
                },
                toStyle: {
                    rotateX: -45,
                    translateY: 0.5,
                    ...TransitionConfig.ORIGIN_CENTER,
                    duration,
                },
                target: TransitionTarget.ENTER,
            }, {
                fromStyle: {
                    rotateX: -45,
                    translateY: 0.5,
                    ...TransitionConfig.ORIGIN_CENTER,
                    ...TransitionConfig.Z_INDEX_LEVEL_1,
                },
                toStyle: {
                    rotateX: 0,
                    translateY: 0,
                    ...TransitionConfig.ORIGIN_TOP_CENTER,
                    ...TransitionConfig.Z_INDEX_LEVEL_1,
                    duration,
                },
                target: TransitionTarget.ENTER,
                label: AnimationLabel.NEXT,
            }];
        }
        if (direction === SideDirectionType.RIGHT) {
            return [{
                fromStyle: {
                    rotateY: 0,
                    translateX: 0,
                    ...TransitionConfig.ORIGIN_LEFT_CENTER,
                },
                toStyle: {
                    rotateY: 45,
                    translateX: 0.5,
                    ...TransitionConfig.ORIGIN_CENTER,
                    duration,
                },
                target: TransitionTarget.EXIT,
            }, {
                fromStyle: {
                    rotateY: 45,
                    translateX: 0.5,
                    ...TransitionConfig.ORIGIN_CENTER,
                },
                toStyle: {
                    rotateY: 90,
                    translateX: 1,
                    ...TransitionConfig.ORIGIN_RIGHT_CENTER,
                    duration,
                },
                target: TransitionTarget.EXIT,
                label: AnimationLabel.NEXT,
            }, {
                fromStyle: {
                    rotateY: -90,
                    translateX: -1,
                    ...TransitionConfig.ORIGIN_LEFT_CENTER,
                },
                toStyle: {
                    rotateY: -45,
                    translateX: -0.5,
                    ...TransitionConfig.ORIGIN_CENTER,
                    duration,
                },
                target: TransitionTarget.ENTER,
            }, {
                fromStyle: {
                    rotateY: -45,
                    translateX: -0.5,
                    ...TransitionConfig.ORIGIN_CENTER,
                    ...TransitionConfig.Z_INDEX_LEVEL_1,
                },
                toStyle: {
                    rotateY: 0,
                    translateX: 0,
                    ...TransitionConfig.ORIGIN_RIGHT_CENTER,
                    ...TransitionConfig.Z_INDEX_LEVEL_1,
                    duration,
                },
                target: TransitionTarget.ENTER,
                label: AnimationLabel.NEXT,
            }];
        }
        return [{
            fromStyle: {
                rotateX: 0,
                translateY: 0,
                ...TransitionConfig.ORIGIN_TOP_CENTER,
            },
            toStyle: {
                rotateX: -45,
                translateY: 0.5,
                ...TransitionConfig.ORIGIN_CENTER,
                duration,
            },
            target: TransitionTarget.EXIT,
        }, {
            fromStyle: {
                rotateX: -45,
                translateY: 0.5,
                ...TransitionConfig.ORIGIN_CENTER,
            },
            toStyle: {
                rotateX: -90,
                translateY: 1,
                ...TransitionConfig.ORIGIN_BOTTOM_CENTER,
                duration,
            },
            target: TransitionTarget.EXIT,
            label: AnimationLabel.NEXT,
        }, {
            fromStyle: {
                rotateX: 90,
                translateY: -1,
                ...TransitionConfig.ORIGIN_TOP_CENTER,
            },
            toStyle: {
                rotateX: 45,
                translateY: -0.5,
                ...TransitionConfig.ORIGIN_CENTER,
                duration,
            },
            target: TransitionTarget.ENTER,
        }, {
            fromStyle: {
                rotateX: 45,
                translateY: -0.5,
                ...TransitionConfig.ORIGIN_CENTER,
                ...TransitionConfig.Z_INDEX_LEVEL_1,
            },
            toStyle: {
                rotateX: 0,
                translateY: 0,
                ...TransitionConfig.ORIGIN_BOTTOM_CENTER,
                ...TransitionConfig.Z_INDEX_LEVEL_1,
                duration,
            },
            target: TransitionTarget.ENTER,
            label: AnimationLabel.NEXT,
        }];
    }
}
